import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Compass, BookOpen } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "@/components/Reveal";
import { SITE, IMAGES } from "@/data/site";

const VALUES = [
    {
        icon: ShieldCheck,
        title: "Discipline over drama",
        copy: "Every product we sell is rules-based and drawdown-capped. We would rather be boring and solvent than exciting and sorry.",
    },
    {
        icon: Compass,
        title: "Transparency by default",
        copy: "A published registered address, reachable humans, and performance language that always includes the drawdown alongside the return.",
    },
    {
        icon: BookOpen,
        title: "Education first",
        copy: "Tools amplify skill — they never replace it. Clients learn exactly what their capital is doing, and why, before it does anything at all.",
    },
];

export default function About() {
    return (
        <>
            <section data-testid="about-hero" className="relative overflow-hidden">
                <div className="pointer-events-none absolute -top-40 left-[-10%] h-[30rem] w-[30rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="mx-auto max-w-7xl px-6 pt-40 pb-20 md:px-10 md:pt-48 md:pb-28">
                    <MaskedLine delay={0.05}>
                        <Eyebrow>About Pocketfolio</Eyebrow>
                    </MaskedLine>
                    <h1 className="mt-8 max-w-4xl font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-balance">
                        <MaskedLine delay={0.2}>Built for traders</MaskedLine>
                        <MaskedLine delay={0.34}>
                            who <span className="font-editorial italic font-light text-gold">measure twice.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.6} className="mt-10 max-w-2xl">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-400">
                            Pocketfolio is a trading research and technology company. We build algorithmic
                            systems, publish high-conviction market signals, and teach the discipline that
                            makes both worth owning — across commodities, stocks and ETFs.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="about-story" className="border-t border-white/10 bg-coal/30">
                <div className="mx-auto grid max-w-7xl gap-16 px-6 py-24 md:px-10 md:py-36 lg:grid-cols-12">
                    <Reveal className="lg:col-span-5">
                        <div className="overflow-hidden border border-white/10">
                            <img
                                src={IMAGES.office}
                                alt="The Pocketfolio desk"
                                data-testid="about-office-image"
                                className="h-full min-h-[320px] w-full object-cover opacity-80"
                            />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7">
                        <Reveal>
                            <Eyebrow>The story</Eyebrow>
                            <h2 className="mt-6 font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                                Research first. Hype never.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-8 space-y-6 text-base leading-relaxed text-zinc-400">
                                <p>
                                    Pocketfolio began with a frustration shared by every serious trader: a
                                    market flooded with bots that had never seen a walk-forward test, signal
                                    channels that disappeared during drawdowns, and 'education' that was really
                                    just marketing. We built the desk we wanted to buy from.
                                </p>
                                <p>
                                    Today our work spans three disciplines. The systems desk designs and
                                    stress-tests expert advisors for gold, crude oil and index markets. The
                                    research desk distils that same analysis into a small number of
                                    high-conviction signals each week. And the education desk turns both into
                                    a curriculum that builds genuinely process-driven traders.
                                </p>
                                <p>
                                    The company is registered in the Union of the Comoros and serves clients
                                    across Europe, the Middle East, Africa and Asia — a deliberately global
                                    structure for a deliberately global market.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section data-testid="about-values" className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
                <Reveal>
                    <Eyebrow>Operating principles</Eyebrow>
                    <h2 className="mt-6 font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                        How we run the desk.
                    </h2>
                </Reveal>
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                    {VALUES.map((v, i) => (
                        <Reveal key={v.title} delay={i * 0.12}>
                            <div
                                data-testid={`about-value-${i + 1}`}
                                className="group h-full border border-white/10 bg-coal/40 p-8 transition-colors duration-300 hover:border-gold/30"
                            >
                                <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold">
                                    <v.icon size={19} />
                                </span>
                                <h3 className="mt-7 font-display text-xl font-extrabold tracking-tight">{v.title}</h3>
                                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{v.copy}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section data-testid="about-registration" className="border-t border-white/10 bg-coal/30">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
                    <Reveal className="grid gap-12 lg:grid-cols-12">
                        <div className="lg:col-span-5">
                            <Eyebrow>Registered office</Eyebrow>
                            <h2 className="mt-6 font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                                A real address. A reachable desk.
                            </h2>
                            <p className="mt-6 text-base leading-relaxed text-zinc-400">
                                Pocketfolio Ltd is registered in the Union of the Comoros. Every enquiry
                                reaches a human on the desk — usually within one business day.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="about-cta-contact"
                                className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-medium text-black transition-colors duration-300 hover:bg-gold-light"
                            >
                                Contact the desk <ArrowRight size={16} />
                            </Link>
                        </div>
                        <div className="lg:col-span-7">
                            <dl className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
                                <div className="bg-obsidian p-8">
                                    <dt className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Registered address</dt>
                                    <dd data-testid="about-address" className="mt-4 text-sm leading-relaxed text-zinc-300">
                                        {SITE.address.join(", ")}
                                    </dd>
                                </div>
                                <div className="bg-obsidian p-8">
                                    <dt className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Contact</dt>
                                    <dd className="mt-4 text-sm text-zinc-300">
                                        <a href={`mailto:${SITE.email}`} data-testid="about-email" className="text-gold hover:text-gold-light transition-colors duration-300">
                                            {SITE.email}
                                        </a>
                                    </dd>
                                </div>
                                <div className="bg-obsidian p-8">
                                    <dt className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Focus markets</dt>
                                    <dd className="mt-4 text-sm text-zinc-300">Commodities · Stocks · ETFs</dd>
                                </div>
                                <div className="bg-obsidian p-8">
                                    <dt className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Operating model</dt>
                                    <dd className="mt-4 text-sm text-zinc-300">Research → Backtest → Incubate → Release</dd>
                                </div>
                            </dl>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
